/**
 * «Porcentajes» de un colaborador (administración, 4/11/2026), al abrirlo
 * desde Colaboradores (solo gestión):
 *  · su % FIJO: el que se le paga sobre lo facturado en todo lo que no tenga
 *    excepción. Vacío = el % general de la clínica por tipo de acto.
 *  · sus EXCEPCIONES POR SERVICIO: otro % para un tratamiento o una cirugía
 *    concretos del tarifario. Una por servicio; se edita el % o se retira
 *    (con confirmación).
 * Cuál manda al registrar un acto: excepción por servicio > excepción por
 * médico (de «Porcentajes y fungibles») > % fijo > % general. El % fijo y
 * cada excepción valen DESDE una fecha (por defecto, hoy) y solo para los
 * actos de esa fecha o posteriores: cambiar el % fijo abre un tramo nuevo
 * desde esa fecha y los actos de antes siguen con el que les tocaba. Lo ya
 * registrado conserva su porcentaje (la liquidación avisa si alguno hoy
 * tocaría otro). Con el colaborador dado de baja no se aplica nada de esto.
 * Nada de la clínica en el código: el % y los servicios vienen del servidor.
 *
 * Props: { medico: {medico_id, medico_nombre, en_lista, activo, pct_fijo},
 *          catalogos: {tratamientos, cirugias}, onCambiado, onAnadir }
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import { textoPorcentaje } from '../../lib/importeEuros';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CLASE_SELECT, hoyLocal, leerPct } from './ListaColaboradoresDialogos';

const RUTA = '/api/colaboradores/excepciones-servicio';
const pctCampo = (v) => (v === null || v === undefined ? '' : textoPorcentaje(v).replace(/\s%$/, ''));
const TIPO_TEXTO = { tratamiento: 'Tratamiento', cirugia: 'Cirugía' };
/** «2099-03-01» → «01/03/2099». */
const fechaLegible = (s) => (/^\d{4}-\d{2}-\d{2}$/.test(s || '') ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : (s || ''));

export default function PorcentajesColaborador({ medico, catalogos, onCambiado, onAnadir }) {
  const medicoId = medico?.medico_id;
  const enLista = !!medico?.en_lista && medico?.activo !== false;
  const [fijo, setFijo] = useState(pctCampo(medico?.pct_fijo));
  const [fijoDesde, setFijoDesde] = useState(hoyLocal());
  const [errorFijo, setErrorFijo] = useState('');
  const [guardandoFijo, setGuardandoFijo] = useState(false);
  const [excs, setExcs] = useState(null);
  const [errorLista, setErrorLista] = useState('');
  const [form, setForm] = useState(null);           // {id?, tipo, servicio_id, pct, desde}
  const [errorForm, setErrorForm] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [confirmando, setConfirmando] = useState(null);
  const turno = useRef(0);

  useEffect(() => { setFijo(pctCampo(medico?.pct_fijo)); }, [medico?.pct_fijo]);

  const cargar = useCallback(() => {
    if (!medicoId || !enLista) return;
    turno.current += 1;
    const mio = turno.current;
    api.get(RUTA, { params: { medico_id: medicoId } })
      .then((r) => { if (turno.current === mio) { setExcs(r.data?.excepciones || []); setErrorLista(''); } })
      .catch((e) => { if (turno.current === mio) setErrorLista(mensajeError(e, 'No se han podido cargar las excepciones.')); });
  }, [medicoId, enLista]);
  useEffect(() => { cargar(); }, [cargar]);

  if (!enLista) {
    return (
      <Card className="cjo-card p-4 space-y-2" data-testid="colab-porcentajes">
        <h3 className="text-sm font-semibold text-[#13231E]">Porcentajes</h3>
        <p className="text-sm text-muted-foreground" data-testid="colab-porcentajes-fuera">
          {medico?.en_lista ? 'Está dado de baja de la lista de colaboradores.' : 'No está en la lista de colaboradores.'}
          {' '}Para ponerle un % fijo o excepciones por servicio, añádelo a la lista.
        </p>
        {onAnadir && (
          <Button type="button" size="sm" variant="outline" onClick={onAnadir} data-testid="colab-porcentajes-anadir">
            <Plus className="w-4 h-4 mr-1" /> Añadir a la lista
          </Button>
        )}
      </Card>
    );
  }

  const guardarFijo = async () => {
    const n = leerPct(fijo);
    if (Number.isNaN(n)) { setErrorFijo('Escribe un número de 0 a 100 (por ejemplo 45 o 47,5), o déjalo vacío.'); return; }
    setErrorFijo(''); setGuardandoFijo(true);
    try {
      await api.put(`/api/colaboradores/medicos/${encodeURIComponent(medicoId)}`,
        { pct_fijo: n, desde: fijoDesde || null });
      toast.success(n === null ? 'Sin % fijo: se aplica el % general' : '% fijo guardado');
      onCambiado?.();
    } catch (e) { setErrorFijo(mensajeError(e, 'No se pudo guardar')); }
    finally { setGuardandoFijo(false); }
  };

  const lista = excs || [];
  const servicios = form?.tipo === 'cirugia' ? (catalogos?.cirugias || []) : (catalogos?.tratamientos || []);
  const ocupados = new Set(lista.filter((e) => e.tipo === form?.tipo).map((e) => e.servicio_id));
  const elegibles = servicios.filter((s) => !ocupados.has(s.id));

  const guardarExc = async () => {
    const n = leerPct(form.pct);
    if (n === null || Number.isNaN(n)) { setErrorForm('Escribe el % de 0 a 100 (por ejemplo 45 o 47,5).'); return; }
    setErrorForm(''); setGuardando(true);
    try {
      if (form.id) {
        await api.put(`${RUTA}/${form.id}`, { pct_medico: n, desde: form.desde || null });
      } else {
        await api.post(RUTA, {
          medico_id: medicoId, tipo: form.tipo, servicio_id: form.servicio_id, pct_medico: n, desde: form.desde || null,
        });
      }
      toast.success('Excepción guardada');
      setForm(null);
      cargar();
      onCambiado?.();
    } catch (e) { setErrorForm(mensajeError(e, 'No se pudo guardar')); }
    finally { setGuardando(false); }
  };

  const retirar = async (e) => {
    try {
      await api.delete(`${RUTA}/${e.id}`);
      toast.success('Excepción retirada');
      setConfirmando(null);
      cargar();
      onCambiado?.();
    } catch (err) { toast.error(mensajeError(err, 'No se pudo retirar')); }
  };

  return (
    <Card className="cjo-card p-4 space-y-4" data-testid="colab-porcentajes">
      <div>
        <h3 className="text-sm font-semibold text-[#13231E]">Porcentajes</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Manda primero la excepción por servicio, luego la excepción por médico, luego el % fijo y, sin nada de eso,
          el % general. Un cambio vale para los actos nuevos: lo ya registrado conserva su porcentaje.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="w-40">
          <Label htmlFor="colab-pct-fijo">% fijo</Label>
          <Input id="colab-pct-fijo" inputMode="decimal" value={fijo} placeholder="Vacío: el general"
            onChange={(e) => { setErrorFijo(''); setFijo(e.target.value); }} className="text-right tabular-nums"
            data-testid="colab-pct-fijo" />
        </div>
        <div>
          <Label htmlFor="colab-pct-fijo-desde">Desde</Label>
          <Input id="colab-pct-fijo-desde" type="date" value={fijoDesde} className="w-40"
            onChange={(e) => { setErrorFijo(''); setFijoDesde(e.target.value); }} data-testid="colab-pct-fijo-desde" />
        </div>
        <Button type="button" size="sm" onClick={guardarFijo} disabled={guardandoFijo}
          className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="colab-pct-fijo-guardar">
          {guardandoFijo && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar % fijo
        </Button>
      </div>
      {medico?.pct_fijo_desde && (
        <p className="text-xs text-muted-foreground" data-testid="colab-pct-fijo-vigente">
          {medico.pct_fijo === null || medico.pct_fijo === undefined ? 'Sin % fijo' : `${textoPorcentaje(medico.pct_fijo)} fijo`}
          {' '}desde el {fechaLegible(medico.pct_fijo_desde)}. Los actos de antes siguen con lo que les tocaba.
        </p>
      )}
      {errorFijo && <p className="text-sm text-red-700" role="alert" data-testid="colab-pct-fijo-error">{errorFijo}</p>}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-[#13231E]">Excepciones por servicio</h4>
          {!form && (
            <Button type="button" size="sm" variant="outline" data-testid="colab-exc-anadir"
              onClick={() => { setErrorForm(''); setForm({ tipo: 'tratamiento', servicio_id: '', pct: '', desde: hoyLocal() }); }}>
              <Plus className="w-4 h-4 mr-1" /> Añadir excepción
            </Button>
          )}
        </div>

        {form && (
          <form className="rounded-md border border-[rgba(46,110,94,0.35)] bg-[rgba(46,110,94,0.04)] p-3 space-y-3"
            onSubmit={(e) => { e.preventDefault(); guardarExc(); }} data-testid="colab-exc-form">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <Label htmlFor="colab-exc-tipo">Tipo</Label>
                <select id="colab-exc-tipo" className={CLASE_SELECT} value={form.tipo} disabled={!!form.id}
                  onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value, servicio_id: '' }))}
                  data-testid="colab-exc-tipo">
                  <option value="tratamiento">Tratamiento</option>
                  <option value="cirugia">Cirugía</option>
                </select>
              </div>
              <div>
                <Label htmlFor="colab-exc-servicio">Servicio</Label>
                {form.id ? (
                  <div className="h-9 flex items-center text-sm">{form.servicio_nombre}</div>
                ) : (
                  <select id="colab-exc-servicio" className={CLASE_SELECT} value={form.servicio_id}
                    onChange={(e) => setForm((f) => ({ ...f, servicio_id: e.target.value }))}
                    data-testid="colab-exc-servicio">
                    <option value="">Elige del tarifario</option>
                    {elegibles.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                )}
              </div>
              <div>
                <Label htmlFor="colab-exc-pct">% del médico</Label>
                <Input id="colab-exc-pct" inputMode="decimal" value={form.pct} className="text-right tabular-nums"
                  onChange={(e) => { setErrorForm(''); setForm((f) => ({ ...f, pct: e.target.value })); }}
                  data-testid="colab-exc-pct" />
              </div>
              <div>
                <Label htmlFor="colab-exc-desde">Desde</Label>
                <Input id="colab-exc-desde" type="date" value={form.desde || ''}
                  onChange={(e) => { setErrorForm(''); setForm((f) => ({ ...f, desde: e.target.value })); }}
                  data-testid="colab-exc-desde" />
              </div>
            </div>
            {errorForm && <p className="text-sm text-red-700" role="alert" data-testid="colab-exc-error">{errorForm}</p>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setForm(null)} disabled={guardando}>Cancelar</Button>
              <Button type="submit" size="sm" disabled={guardando || (!form.id && !form.servicio_id)}
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="colab-exc-guardar">
                {guardando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
              </Button>
            </div>
          </form>
        )}

        {errorLista && <p className="text-sm text-red-700" role="alert" data-testid="colab-exc-lista-error">{errorLista}</p>}
        {!excs && !errorLista && (
          <p className="text-sm text-muted-foreground" role="status">Cargando…</p>
        )}
        {excs && (
          <table className="w-full text-sm" data-testid="colab-exc-tabla">
            <tbody>
              {lista.map((e) => (confirmando === e.id ? (
                <tr key={e.id} className="border-b border-border/60" data-testid="colab-exc-fila">
                  <td colSpan={4} className="py-2">
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950"
                      role="alertdialog" aria-label="Retirar excepción" data-testid="colab-exc-confirmar">
                      <p className="text-sm">
                        <span className="font-semibold">¿Retirar la excepción de «{e.servicio_nombre}»?</span>{' '}
                        Los actos nuevos de ese servicio irán con el % que toque (excepción por médico, % fijo o
                        general). Lo ya registrado conserva su porcentaje.
                      </p>
                      <div className="mt-2 flex justify-end gap-2">
                        <Button type="button" size="sm" variant="ghost" onClick={() => setConfirmando(null)}>Cancelar</Button>
                        <Button type="button" size="sm" variant="destructive" onClick={() => retirar(e)}
                          data-testid="colab-exc-confirmar-retirar">Retirar</Button>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={e.id} className="border-b border-border/60" data-testid="colab-exc-fila">
                  <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">{TIPO_TEXTO[e.tipo] || e.tipo}</td>
                  <td className="py-2 px-3">
                    {e.servicio_nombre}
                    {e.desde && (
                      <span className="ml-1 text-xs text-muted-foreground" data-testid="colab-exc-fila-desde">
                        desde {fechaLegible(e.desde)}
                      </span>
                    )}
                    {e.servicio_vigente === false && (
                      <span className="ml-1 text-xs text-amber-800">(ya no está en el tarifario)</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right tabular-nums font-semibold" data-testid="colab-exc-fila-pct">
                    {textoPorcentaje(e.pct_medico)}
                  </td>
                  <td className="py-1 pl-3">
                    <div className="flex justify-end gap-0.5">
                      <Button type="button" size="sm" variant="ghost" title="Editar el %"
                        aria-label={`Editar el % de ${e.servicio_nombre}`} data-testid="colab-exc-editar"
                        onClick={() => { setErrorForm(''); setForm({ id: e.id, tipo: e.tipo, servicio_id: e.servicio_id, servicio_nombre: e.servicio_nombre, pct: pctCampo(e.pct_medico), desde: e.desde || '' }); }}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button type="button" size="sm" variant="ghost" className="text-destructive" title="Retirar"
                        aria-label={`Retirar la excepción de ${e.servicio_nombre}`} data-testid="colab-exc-retirar"
                        onClick={() => setConfirmando(e.id)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              )))}
              {lista.length === 0 && (
                <tr><td colSpan={4} className="py-3 text-center text-sm text-muted-foreground" data-testid="colab-exc-vacio">
                  Sin excepciones por servicio.
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}
